/**
 * The Database screen's own controls, drawn from the containers design
 * system's recipes (`ds-toolbar-btn`, `ds-popover`, `ds-section-label`,
 * `ds-field`): the outlined toolbar action, the menu it opens, and the search
 * field of the explorer and the filter. Kept together so the menus on the
 * screen cannot drift apart from each other.
 */
import { forwardRef } from 'react';

export const ToolbarButton = ({ icon, onClick, isActive, isDisabled, title, className = '', children }: {
  icon?: React.ReactNode,
  onClick?: () => void,
  /** A menu this button opens is open. */
  isActive?: boolean,
  isDisabled?: boolean,
  title?: string,
  className?: string,
  children: React.ReactNode,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={isDisabled}
    title={title}
    aria-expanded={isActive}
    className={[
      'b4-focus inline-flex items-center justify-center gap-2 h-11 px-[18px] rounded-surface border',
      'font-inter text-[length:calc(14px*var(--b4-type,1))] whitespace-nowrap bg-transparent transition-colors duration-[120ms]',
      isDisabled
        ? 'border-navy-600 text-navy-300 cursor-not-allowed'
        : isActive
          ? 'border-action text-navy-100'
          : 'border-navy-500 text-navy-100 hover:border-navy-400 hover:text-white',
      className,
    ].join(' ')}
  >
    {icon}
    {children}
  </button>
);

/** The menu surface under a control. */
export const Popover = ({ align = 'left', width, className = '', children }: {
  align?: 'left' | 'right',
  width?: number,
  className?: string,
  children: React.ReactNode,
}) => (
  <div
    role="menu"
    style={width ? { width } : undefined}
    className={[
      'absolute top-[calc(100%+6px)] z-30 max-w-[calc(100vw-32px)] py-1 rounded-card border border-navy-500 bg-navy-800',
      'shadow-[0_12px_32px_rgba(0,0,0,.45)] overflow-hidden',
      align === 'right' ? 'right-0' : 'left-0',
      className,
    ].join(' ')}
  >
    {children}
  </div>
);

/** A row inside a Popover. */
export const PopoverItem = ({ icon, onClick, isDisabled, meta, children, ...aria }: {
  icon?: React.ReactNode,
  onClick: () => void,
  isDisabled?: boolean,
  meta?: React.ReactNode,
  children: React.ReactNode,
  role?: string,
  'aria-checked'?: boolean,
}) => (
  <button
    type="button"
    role="menuitem"
    {...aria}
    onClick={onClick}
    disabled={isDisabled}
    className={[
      'b4-focus flex items-center gap-2.5 w-full h-[38px] px-3.5 text-left font-inter text-ds-135 text-navy-200 transition-colors duration-[120ms]',
      isDisabled ? 'cursor-not-allowed' : 'hover:bg-navy-600 hover:text-white',
    ].join(' ')}
  >
    {icon}
    <span className="min-w-0 flex-1 truncate">{children}</span>
    {meta}
  </button>
);

/** Courrier group label inside a Popover. */
export const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="block px-3.5 pt-2.5 pb-1.5 font-courrier text-ds-105 uppercase tracking-[0.14em] leading-none text-navy-300">
    {children}
  </span>
);

/** `ds-field` — a navy-800 control with an icon before the input. */
export const SearchField = forwardRef<HTMLInputElement, {
  prefix?: React.ReactNode,
  suffix?: React.ReactNode,
  isInvalid?: boolean,
  className?: string,
  inputClassName?: string,
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'>>(({ prefix, suffix, isInvalid, className = '', inputClassName = '', ...input }, ref) => (
  <div
    className={[
      'flex items-center gap-2 h-11 lg:h-[38px] px-3 rounded-control border bg-navy-800 transition-colors duration-[120ms]',
      'focus-within:border-action focus-within:shadow-[0_0_0_3px_rgba(32,138,236,.25)]',
      isInvalid ? 'border-state-err' : 'border-navy-400 hover:border-navy-300',
      className,
    ].join(' ')}
  >
    {prefix ? <span className="flex-none text-navy-300">{prefix}</span> : null}
    <input
      ref={ref}
      {...input}
      className={`w-full min-w-0 bg-transparent outline-none border-0 text-white placeholder:text-navy-300 text-[16px] lg:text-ds-135 ${inputClassName}`}
    />
    {suffix}
  </div>
));

SearchField.displayName = 'SearchField';
